import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Divider,
  Grid,
  makeStyles,
  TablePagination,
  Typography,
  Checkbox,
  Button,
  CircularProgress,
} from "@material-ui/core";
import styles from "features/settings/styles";
import { useMutationWithErrorHandling, useQueryWithErrorHandling } from "hooks/errorHandling";
import { STRING_TRANSLATIONS } from "../queries";
import { useHistory, useParams } from "react-router";
import { Card, CustomTextField, LoadingFakeText } from "@totalsoft/rocket-webapp-components-deprecated";
import { useChangeTrackingLens } from "@totalsoft/change-tracking-react";
import { get, sequence, set, useStateLens } from "@totalsoft/react-state-lens";
import ScrollbarWrapper from "features/common/components/ScrollbarWrapper";
import { onCheckBoxChange, onTextBoxChange } from "utils/propertyChangeAdapters";
import { isDirty } from "@totalsoft/change-tracking";
import { useContentFooter } from "hooks/mainContent";
import CommandsCard from "features/common/components/CommandsCard";
import { SAVE_STRING_TRANSLATIONS } from "../mutations";
import { emptyArray, emptyString } from "constants/defaultValues";
import useDebounce from "hooks/useDebounce";
import * as R from "ramda";
import SectionTitle from "features/common/components/SectionTitle";
import CheckIcon from "@material-ui/icons/Check";
import { ArrowBack } from "@material-ui/icons";
import TranslationAdd from "./TranslationAdd";
import { getTenant } from "providers/TenantProvider";
const useStyles = makeStyles(styles);

const TranslationContainer = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { languageCode } = useParams();
  const [, setFooter] = useContentFooter();
  const history = useHistory();
  const localStateLens = useStateLens({
    page: 0,
    rowsPerPage: 10,
    filterText: "",
    showStringsWithoutValue: false,
  });
  const localState = localStateLens |> get;
  const localStateDebounced = useDebounce(localState, 300);

  const [stringTranslationsLens, stringTranslationsDirtyInfo, resetStringTranslationsLens] = useChangeTrackingLens();
  const stringTranslationsIsDirty = isDirty(stringTranslationsDirtyInfo);
  const stringTranslations = stringTranslationsLens |> get;

  const [visibleLenses, setVisibleLenses] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  const [isElementAdded, setIsElementAdded] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const { countryCode } = getTenant();

  const filterVisibleLenses = useMemo(() => {
    return (stringTranslationsLens.strings |> sequence).filter((lens, index) => {
      let lensIsDirty = false;
      if (stringTranslationsIsDirty && stringTranslationsDirtyInfo?.strings[index]?.value !== undefined)
        lensIsDirty = stringTranslationsDirtyInfo?.strings[index]?.value;
      return (
        ((lens.key |> get).toLowerCase().includes(localStateDebounced.filterText.toLowerCase()) ||
          (lens.defaultValue |> get).toLowerCase().includes(localStateDebounced.filterText.toLowerCase()) ||
          ((lens.value |> get) !== null &&
            (lens.value |> get).toLowerCase().includes(localStateDebounced.filterText.toLowerCase()))) &&
        (((localStateLens.showStringsWithoutValue |> get) &&
          (lensIsDirty || (lens.value |> get) === null || (lens.value |> get) === emptyString)) ||
          !(localStateLens.showStringsWithoutValue |> get))
      );
    });
  }, [
    stringTranslationsLens.strings,
    localStateDebounced.filterText,
    localStateLens?.showStringsWithoutValue,
    stringTranslationsDirtyInfo.strings,
    stringTranslationsIsDirty,
  ]);

  useEffect(() => {
    if (isElementAdded || !stringTranslationsIsDirty || isSearching) {
      setVisibleLenses(filterVisibleLenses);
      setIsSearching(false);
    }
  }, [filterVisibleLenses, stringTranslationsIsDirty, isSearching, isElementAdded, stringTranslationsLens.strings]);

  const currentPageLenses = useMemo(
    () => R.splitEvery(localState.rowsPerPage, visibleLenses)[localState.page] || emptyArray,
    [localState.rowsPerPage, visibleLenses, localState.page]
  );

  const { data: stringTranslationsData, loading } = useQueryWithErrorHandling(STRING_TRANSLATIONS, {
    variables: { languageCode },
  });

  const [saveStringTranslations, { loading: saving }] = useMutationWithErrorHandling(SAVE_STRING_TRANSLATIONS);

  const handleSave = useCallback(async () => {
    const dirtyLenses = (stringTranslationsLens.strings |> sequence)
      .map((lens, index) => ({
        lens,
        isDirty: isDirty(stringTranslationsDirtyInfo.strings[index]),
      }))
      .filter((x) => x.isDirty);
    const translationFields = dirtyLenses.map((lens) => ({
      countryCode: lens.lens.countryCode |> get,
      key: lens.lens.key |> get,
      value: lens.lens.value |> get,
    }));
    const { data } = await saveStringTranslations({ variables: { input: { languageCode, translationFields } } });
    resetStringTranslationsLens(data?.saveStringTranslations);
  }, [
    stringTranslationsLens.strings,
    saveStringTranslations,
    languageCode,
    resetStringTranslationsLens,
    stringTranslationsDirtyInfo.strings,
  ]);

  const handleChangePage = useCallback(
    (event, newPage) => {
      (localStateLens.page |> set)(newPage);
    },
    [localStateLens.page]
  );

  const handleChangeRowsPerPage = useCallback(
    (event) => {
      (localStateLens.rowsPerPage |> set)(parseInt(event.target.value, 10));
      (localStateLens.page |> set)(0);
    },
    [localStateLens.rowsPerPage, localStateLens.page]
  );

  const handleChangeFilter = useCallback(
    (filter) => (value) => {
      (filter |> set)(value);
      (localStateLens.page |> set)(0);
      setIsSearching(true);
    },
    [localStateLens.page]
  );

  const onOpenModal = useCallback(() => {
    setShowModal(true);
    const newItem = {
      countryCode: countryCode,
      key: "",
      value: "",
      defaultValue: "",
    };
    const updatedTranslations = stringTranslationsLens.strings |> get;
    const newTranslations = [...updatedTranslations, newItem];
    (stringTranslationsLens.strings |> set)(newTranslations);
  }, [countryCode, stringTranslationsLens.strings]);

  const onCloseModal = useCallback(() => {
    setShowModal(false);
  }, []);

  useEffect(() => {
    if (stringTranslationsIsDirty || !stringTranslationsData?.stringTranslations) return;
    resetStringTranslationsLens(stringTranslationsData?.stringTranslations);
  }, [resetStringTranslationsLens, stringTranslationsIsDirty, stringTranslationsData?.stringTranslations]);

  const handleBack = useCallback(() => {
    history.push({ pathname: `/settings` });
  }, [history]);

  useEffect(() => {
    setFooter(
      <CommandsCard
        buttonLeft={
          <Button variant="contained" color="primary" endIcon={<ArrowBack />} onClick={handleBack}>
            {t("General.Back")}
          </Button>
        }
        buttonRight={
          <Grid container spacing={1}>
            <Grid item>
              <Button
                variant="contained"
                color="primary"
                onClick={onOpenModal}
                disabled={saving}
                endIcon={<CheckIcon />}
              >
                {t("Settings.Translations.Add")}
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSave}
                disabled={!isDirty(stringTranslationsDirtyInfo) || saving}
                endIcon={saving ? <CircularProgress size={20} color={"inherit"} /> : <CheckIcon />}
              >
                {t("ProfilePage.Save")}
              </Button>
            </Grid>
          </Grid>
        }
      />
    );
  }, [t, setFooter, handleSave, stringTranslationsDirtyInfo, saving, handleBack, onOpenModal]);

  if (loading || !stringTranslations) return <LoadingFakeText />;

  return (
    <>
      <ScrollbarWrapper className={classes.scrollbar}>
        <Card className={classes.card}>
          <SectionTitle title={`${t("Settings.Translations.Title")}: ${languageCode}`} />
          <Grid container spacing={3} alignItems="center" alignContent="center">
            <Grid item xs={true}>
              <CustomTextField
                label={t("Settings.Translations.Filter")}
                fullWidth
                value={(localStateLens.filterText |> get) || emptyString}
                onChange={handleChangeFilter(localStateLens.filterText) |> onTextBoxChange}
              />
            </Grid>
            <Grid item xs={"auto"}>
              <Checkbox
                checked={localStateLens?.showStringsWithoutValue |> get}
                onChange={handleChangeFilter(localStateLens.showStringsWithoutValue) |> onCheckBoxChange}
              ></Checkbox>
            </Grid>
            <Grid item xs={"auto"}>
              <Typography variant="h6">{t("Settings.Translations.ShowStringsWithoutValue")}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            {currentPageLenses.map((translationFieldLens, index) => (
              <Grid item container key={index} spacing={1}>
                <Grid item xs={12} md={2}>
                  <Typography className={classes.keyTypography}> {t("Settings.Translations.Key")}</Typography>
                </Grid>
                <Grid item xs={12} md={10}>
                  <Typography className={classes.valueTypography}>{translationFieldLens.key |> get}</Typography>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Typography className={classes.keyTypography}> {t("Settings.Translations.DefaultValue")}</Typography>
                </Grid>
                <Grid item xs={12} md={10}>
                  <Typography className={classes.valueTypography}>
                    {translationFieldLens.defaultValue |> get}
                  </Typography>
                </Grid>
                {(translationFieldLens.defaultValue |> get).match(/\{{(.*?)\}}/g) && (
                  <Grid item container>
                    <Grid item xs={12} md={2}>
                      <Typography className={classes.keyTypography}>{t("Settings.Translations.Bookmarks")}</Typography>
                    </Grid>
                    <Grid item xs={12} md={10}>
                      <Typography className={classes.valueTypography}>
                        {(translationFieldLens.defaultValue |> get).match(/\{{(.*?)\}}/g).join(", ")}
                      </Typography>
                    </Grid>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <CustomTextField
                    label={t("Settings.Translations.Value")}
                    fullWidth
                    multiline
                    variant="outlined"
                    value={(translationFieldLens.value |> get) || emptyString}
                    onChange={translationFieldLens.value |> set |> onTextBoxChange}
                  />
                </Grid>
              </Grid>
            ))}
          </Grid>
          <TablePagination
            component="div"
            count={visibleLenses.length}
            page={localState.page}
            onPageChange={handleChangePage}
            rowsPerPage={localState.rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage={t("RowsPerPage")}
          />
        </Card>
      </ScrollbarWrapper>
      {showModal && (
        <TranslationAdd
          stringTranslationLens={(stringTranslationsLens.strings |> sequence).slice(-1)[0]}
          open={showModal}
          onCloseModal={onCloseModal}
          setIsElementAdded={setIsElementAdded}
          stringTranslationsDirtyInfo={stringTranslationsDirtyInfo}
          stringTranslationsLens={stringTranslationsLens}
        />
      )}
    </>
  );
};

export default TranslationContainer;
